import React from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { IoCalendarOutline, IoBusinessOutline, IoDocumentTextOutline, IoNewspaperOutline, IoCardOutline } from 'react-icons/io5';
import { formatDateOnly } from '../utils/dateUtils';

export interface LinkedContent {
  id: string;
  type: 'review' | 'work_experience' | 'note' | 'career_card' | 'work_experience_document';
  preview: {
    title?: string;
    company?: string;
    position?: string;
    date?: string;
    date_range?: {
      start: string;
      end?: string;
    };
    preview?: string;
  };
}

interface LinkedContentPreviewProps {
  content: LinkedContent;
  isLoading?: boolean;
  onUnlink?: () => void;
  className?: string;
}

export const LinkedContentPreview: React.FC<LinkedContentPreviewProps> = ({
  content,
  isLoading = false,
  onUnlink,
  className = '',
}) => {
  const { darkMode } = useDarkMode();

  if (isLoading) {
    return (
      <div 
        className={`
          animate-pulse rounded-lg p-4
          ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}
          ${className}
        `}
      >
        <div className="h-4 bg-gray-400 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-400 rounded w-1/2 mb-2"></div>
        <div className="h-3 bg-gray-400 rounded w-2/3"></div>
      </div>
    );
  }

  const renderContent = () => {
    switch (content.type) {
      case 'review':
        return (
          <>
            <div className="flex items-center gap-2 mb-1">
              <IoDocumentTextOutline className="text-purple-500" />
              <h3 className="font-medium">{content.preview.title || 'Untitled Review'}</h3>
            </div>
            {content.preview.date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                <IoCalendarOutline />
                <span>{formatDateOnly(content.preview.date)}</span>
              </div>
            )}
            {content.preview.preview && (
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {content.preview.preview}
              </p>
            )}
          </>
        );

      case 'note':
        return (
          <>
            <div className="flex items-center gap-2 mb-1">
              <IoNewspaperOutline className="text-green-500" />
              <h3 className="font-medium">{content.preview.title || 'Untitled Note'}</h3>
            </div>
            {content.preview.date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                <IoCalendarOutline />
                <span>{formatDateOnly(content.preview.date)}</span>
              </div>
            )}
            {content.preview.preview && (
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {content.preview.preview}
              </p>
            )}
          </>
        );

      case 'career_card':
        return (
          <>
            <div className="flex items-center gap-2 mb-1">
              <IoCardOutline className="text-blue-500" />
              <h3 className="font-medium">{content.preview.title || 'Untitled Card'}</h3>
            </div>
            {content.preview.date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                <IoCalendarOutline />
                <span>{formatDateOnly(content.preview.date)}</span>
              </div>
            )}
            {content.preview.preview && (
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {content.preview.preview}
              </p>
            )}
          </>
        );

      case 'work_experience':
        return (
          <>
            <div className="flex items-center gap-2 mb-1">
              <IoBusinessOutline className="text-blue-500" />
              <h3 className="font-medium">{content.preview.position || 'Position'}</h3>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2 mb-1">
                <span>{content.preview.company || 'Company'}</span>
              </div>
              {content.preview.date_range && (
                <div className="flex items-center gap-2">
                  <IoCalendarOutline />
                  <span>
                    {formatDateOnly(content.preview.date_range.start)}
                    {content.preview.date_range.end && ` - ${formatDateOnly(content.preview.date_range.end)}`}
                  </span>
                </div>
              )}
            </div>
          </>
        );
    }
  };

  return (
    <div 
      className={`
        rounded-lg p-4 relative group
        ${darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-100 hover:bg-gray-200'}
        transition-colors duration-200
        ${className}
      `}
    >
      {renderContent()}
      {onUnlink && (
        <button
          onClick={onUnlink}
          className={`
            absolute top-2 right-2 p-1 rounded-full
            opacity-0 group-hover:opacity-100
            transition-opacity duration-200
            hover:bg-gray-300 dark:hover:bg-gray-700
            text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
          `}
          aria-label="Unlink content"
        >
          <svg 
            className="w-4 h-4"
            fill="none" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default LinkedContentPreview; 